import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import produce from 'immer';
import queryString from 'query-string';
import axios from 'axios';
import { Property} from './types';
import debounce from 'lodash.debounce';
import ConfirmModal from '../ConfirmModal';
import PropertyDisplay from './PropertyDisplay';
import { FiTrash2 } from 'react-icons/fi';
import { Icon } from 'semantic-ui-react';
import { Match } from './types';
// import AddAdditionalData from './AddAdditionalData';
import { EditIcon } from '../../../components/icons';
import AddAdditionalData from '../../OrderEntry/PropertiesAndNames/PropertyTab/AddAdditionalData';
import { useAddProperty } from './AddPropertyContext';

const CollapsedCard = styled.div<{ error: boolean }>`
  border: ${props => (props.error ? '1px solid #ff5252' : '1px solid #e5e5ea')};
  border-radius: 5px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  margin-bottom: 8px;
  margin-top: 8px;
`;

const Overlay = styled.div`
  background-color: transparent;
  bottom: 0;
  height: 100%;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  width: 100%;
  z-index: 100;
`;

const baseProperty: Property = {
  address: '',
  addressLine2: '',
  undeterminedLocality: false,
  zip: '',
  district: '',
  section: '',
  block: '',
  lot: '',
  qualifier: '',
  isDuplicate: false,
  includeMunicipalSearches: false,
  isValidated: false,
  rpadId: null,
  municipalityId: null,
  subMunicipalityId: null,
  duplicates: null,
  countyOnly: false,
  rpadPropertyId: '',
  printKey: '',
  owners: null,
  fullValidationNote: null,
  validationNote: '',
  preConvertedFiles: null,
  validationFiles: null,
  isResidential: null,
  unvalidatedOwner: null,
  selectedCounty: null,
  overrideMode: false,
  isNyc: false,
  selectedCity: {
    county: '',
    municipality: '',
    municipalityId: null,
    municipalityType: '',
    stateCode: '',
    stateName: '',
    subMunicipality: '',
    subMunicipalityId: null,
    subMunicipalityType: '',
    swisCode: '',
    rpadSwisCode: '',
    countyId: null,
    id: null,
    municipalityTypeId: null,
    subMunicipalityTypeId: null,
    hamletId: null,
    hamletName: '',
  },
};

interface Props {}
const Rpad = (props: Props) => {
  const {
    setProperties,
    properties,
    validateProperty,
    propertyErrors,
  } = useAddProperty();
  const [searchWasMade, setSearchWasMade] = useState(false);
  const [matches, setMatches] = React.useState<Match>({ totalCount: null, results: [] });
  const [searchText, setSearchText] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState(-1);
  const [editIndex, setEditIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);

  const setPropertyInfo = (property: Property, index: number) => {
    const newProperties = produce(properties, draft => {
      draft[index] = property;
    });
    setProperties(newProperties);
  };
  const addProperty = () => {
    const newProperties = [...properties];
    newProperties.push(baseProperty);
    setProperties(newProperties);
  };

  const deleteProperty = (indexToRemove: number) => {
    const newProperties = properties?.filter((p, index) => index !== indexToRemove);
    const owners = properties[indexToRemove].owners;
    setProperties(newProperties);
  };

  const submitProperty = () => {
    const err = validateProperty();
    if (!err) {
      addProperty();
    }
  };
  const boroMapper = {
    Manhattan: '1',
    Bronx: '2',
    Brooklyn: '3',
    Queens: '4',
    'Staten Island': '5',
  };
  const getNumberOfBorough = boroughName => {
    return boroMapper[boroughName];
  };

  const callRpadValidation = async (searchTerm: string, property: Property, returnCount: number = 15) => {
    if (!searchTerm) {
      setMatches({ totalCount: 0, results: [] });
      return;
    }
    const queryOptions = {
      address: searchTerm,
      swiss: property.selectedCity?.swisCode,
      borough: getNumberOfBorough(property.selectedCity?.municipality),
      returnCount,
    };

    const stringifiedParams = queryString.stringify(queryOptions);
    const baseUrl = `/api/clientproperty/validateRpad/${
      !property?.selectedCity?.stateCode
        ? 'default'
        : property?.selectedCity?.stateCode === 'NJ'
        ? 'NJ'
        : property?.selectedCity?.municipalityType === 'Borough'
        ? 'NYC'
        : 'NYS'
    }`;
    const { data } = await axios.get(`${baseUrl}?${stringifiedParams}`);
    setMatches(data);
  };
  const findMatches = useCallback(
    debounce(async (e: any, property: Property) => {
      setIsLoading(true);
      e.preventDefault();
      setSearchText(e.target.value);
      setSearchWasMade(true);
      await callRpadValidation(e.target.value, property);
      setIsLoading(false);
    }, 600),
    []
  );
  useEffect(() => {
    if (properties.length) {
      setEditIndex(properties.length - 1);
    }
  }, [properties.length]);

  return (
    <div style={{ width: 700 }}>
      {matches.results.length > 0 && (
        <Overlay onClick={() => setMatches({ totalCount: null, results: [] })} />
      )}
      {properties?.map((p, index, arr) => {
        return (
          <div>
            {index === editIndex ? (
              <PropertyDisplay
                index={index}
                setDeleteConfirmation={setDeleteConfirmation}
                property={p}
                setEditIndex={setEditIndex}
                findMatches={findMatches}
                callRpadValidation={callRpadValidation}
                matches={matches}
                searchText={searchText}
                setPropertyInfo={setPropertyInfo}
                setMatches={setMatches}
                isLoading={isLoading}
              />
            ) : (
              <CollapsedCard error={Boolean(propertyErrors[index])} onClick={() => setEditIndex(index)}>
                <div style={{ flexDirection: 'column' }}>
                  <div>{p.address}</div>
                  <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                    {p.selectedCity.county}
                    <span style={{ paddingLeft: 4 }}>{p.selectedCity.municipality}</span>
                  </div>
                  <div>
                    {p.section && p.section}
                    <span style={{ paddingLeft: 4, paddingRight: 4 }}>{p.block}</span>
                    {p.lot}
                    {p.qualifier && p.qualifier}
                  </div>
                </div>
                <div style={{ display: 'flex' }}>
                  <FiTrash2
                    style={{ height: 23, width: 23 }}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setDeleteConfirmation(index);
                    }}
                  />
                  <span style={{ marginLeft: 24 }}>
                    <EditIcon onClick={() => setEditIndex(index)} />
                  </span>
                </div>
              </CollapsedCard>
            )}
          </div>
        );
      })}
      {deleteConfirmation > -1 && (
        <ConfirmModal
          onConfirmAction={() => {
            deleteProperty(deleteConfirmation);
            setDeleteConfirmation(-1);
          }}
          buttonText="Delete"
          header="Delete Property"
          cancelButtonText="Cancel"
          open={deleteConfirmation > -1}
          onClose={() => setDeleteConfirmation(-1)}
          height={265}
          width={400}
        >
          Are you sure you want to delete this property?
        </ConfirmModal>
      )}
      <div>
        <AddAdditionalData
          dataName="Add another property"
          styles={{ marginTop: 24 }}
          onClick={submitProperty}
        />
      </div>
    </div>
  );
};

export default Rpad;
